import React, { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import {
  ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Landmark,
  Mail, Phone, User, Target, ChevronDown, Loader2, Sparkles,
  Building2,
} from 'lucide-react';
import './ServiceDetailExtras.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PHOTO TESTIMONIALS
 * All real client receipts — credit lifts, loan payoffs, discharge letters, thank-you texts.
 * Served from /testimonials in /app/frontend/public.
 */
const PHOTO_TESTIMONIALS = [
  {
    src: '/testimonials/experian-791-text.jpg',
    quote: "OMG THIS IS UNREAL — Experian 791, +123 pts. I think I may cry. THANK YOU MR. Collins and the team.",
    name: 'Collins Client',
    result: 'Experian 791 · +123 pts',
  },
  {
    src: '/testimonials/score-plus-114.jpg',
    quote: '+114 points in 5 days.',
    name: 'Marcus',
    result: '5-day score jump',
  },
  {
    src: '/testimonials/score-688.jpg',
    quote: '+105 pts today — TransUnion 688. Bro.',
    name: 'DeAndre',
    result: '105-point lift',
  },
  {
    src: '/testimonials/score-739.jpg',
    quote: 'Woke up to 101 pts up — now at 739.',
    name: 'Jalen',
    result: 'Score now 739 (Good)',
  },
  {
    src: '/testimonials/mohela-forgiven.jpg',
    quote: 'Your student loans have been forgiven — MOHELA, PSLF/TEPSLF. Thank you for your public service.',
    name: 'MOHELA Client',
    result: 'Federal loans forgiven',
  },
  {
    src: '/testimonials/slr/slr-03.jpg',
    quote: 'Borrower Defense Loan Discharge — Completed. $12,734 gone.',
    name: 'Borrower Defense Win',
    result: '$12,734 discharged',
  },
  {
    src: '/testimonials/slr/slr-02.jpg',
    quote: "Congratulations! You've paid off all your loans. Total Balance $0.",
    name: 'Nelnet Payoff',
    result: '$0 total balance',
  },
  {
    src: '/testimonials/slr/slr-01.jpg',
    quote: "You helped clear over $12,000 debt in student loans. Thanks again — you're the best.",
    name: '@thereallamarqus',
    result: '$12K+ cleared',
  },
];

// Re-exported so the HomePage can reuse the full receipt wall
export const ALL_PHOTO_TESTIMONIALS = PHOTO_TESTIMONIALS;

/**
 * VIDEO TESTIMONIALS
 * 2 real client-shared videos.
 */
const VIDEO_TESTIMONIALS = [
  {
    src: '/testimonials/client-success.mp4',
    quote: '"The score moved exactly like they said it would — approval the same week."',
    name: 'Verified Client',
    result: 'Funding approval secured',
  },
  {
    src: '/testimonials/mina-voice.mp4',
    quote: '"Y\'all cleared my file in weeks — I\'m still in shock. Thank you."',
    name: 'Mina',
    result: 'File cleared in weeks',
  },
];

/* -------- Photo Testimonial Carousel -------- */
export const TestimonialCarousel = ({ items, eyebrow, title, subtitle }) => {
  const slides = items && items.length ? items : PHOTO_TESTIMONIALS;
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center', skipSnaps: false },
    [Autoplay({ delay: 5500, stopOnInteraction: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  return (
    <section className="sde-testi" data-testid="sde-testimonials">
      <div className="sde-testi-head">
        <span className="sde-eyebrow sde-eyebrow-gold">
          <Sparkles size={13} /> {eyebrow || 'Real Client Results'}
        </span>
        <h2 className="sde-section-title">
          {title || <>Receipts, not <span className="sde-gold">promises</span>.</>}
        </h2>
        <p className="sde-section-sub">
          {subtitle || 'Real score-jump screenshots clients sent us straight from their Credit Karma, SmartCredit, and VantageScore apps — not stock photos, not actors.'}
        </p>
      </div>

      <div className="sde-embla">
        <div className="sde-embla-viewport" ref={emblaRef}>
          <div className="sde-embla-container">
            {slides.map((t, i) => (
              <div className="sde-embla-slide" key={i} data-testid={`sde-testi-${i}`}>
                <div className="sde-testi-card">
                  <div className="sde-testi-media">
                    <img
                      src={t.src}
                      alt={`${t.name} — ${t.result}`}
                      loading="lazy"
                      className="sde-testi-img"
                    />
                  </div>
                  <div className="sde-testi-body">
                    <p className="sde-testi-quote">"{t.quote}"</p>
                    <div className="sde-testi-foot">
                      <span className="sde-testi-name">{t.name}</span>
                      <span className="sde-testi-dot" />
                      <span className="sde-testi-result">{t.result}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sde-embla-controls">
          <button
            type="button"
            className="sde-embla-btn"
            onClick={scrollPrev}
            aria-label="Previous testimonial"
            data-testid="sde-testi-prev"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="sde-embla-dots">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`sde-embla-dot${i === selectedIndex ? ' is-active' : ''}`}
                aria-hidden="true"
              />
            ))}
          </div>
          <button
            type="button"
            className="sde-embla-btn"
            onClick={scrollNext}
            aria-label="Next testimonial"
            data-testid="sde-testi-next"
          >
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* -------- Video Testimonials -------- */
export const VideoTestimonials = ({ items, eyebrow, title, subtitle }) => {
  const list = items && items.length ? items : VIDEO_TESTIMONIALS;
  return (
    <section className="sde-vidtesti" data-testid="sde-video-testimonials">
      <div className="sde-testi-head">
        <span className="sde-eyebrow sde-eyebrow-gold">
          <Sparkles size={13} /> {eyebrow || 'Clients In Their Own Words'}
        </span>
        <h2 className="sde-section-title">
          {title || <>Hear it from the <span className="sde-gold">people who lived it</span>.</>}
        </h2>
        <p className="sde-section-sub">
          {subtitle || "Real clients, filmed on their own phones, after the file was cleared and the funding came in. No scripts, no actors."}
        </p>
      </div>
      <div className="sde-vidtesti-grid">
        {list.map((v, i) => (
          <div className="sde-vidtesti-card" key={i} data-testid={`sde-vidtesti-${i}`}>
            <div className="sde-vidtesti-media">
              <video
                src={v.src}
                controls
                playsInline
                preload="metadata"
                className="sde-vidtesti-vid"
              />
            </div>
            <div className="sde-testi-body">
              <p className="sde-testi-quote">{v.quote}</p>
              <div className="sde-testi-foot">
                <span className="sde-testi-name">{v.name}</span>
                <span className="sde-testi-dot" />
                <span className="sde-testi-result">{v.result}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* -------- Dispute Everywhere -------- */
export const DisputeEverywhere = () => (
  <section className="sde-dispute" data-testid="sde-dispute">
    <div className="sde-dispute-head">
      <span className="sde-eyebrow">Our Removal Methodology</span>
      <h2 className="sde-section-title">
        We don't just challenge the bureaus — <span className="sde-gold">we dispute everywhere.</span>
      </h2>
      <p className="sde-section-sub">
        Most repair shops send a few form letters to Equifax, Experian and TransUnion and call
        it a day. That's why their results stall. We go deeper — every entity that has your data
        gets challenged, in parallel, until the item is gone from every source feeding your file.
      </p>
    </div>

    <div className="sde-dispute-grid">
      {[
        { title: 'The 3 Credit Bureaus', body: 'Equifax, Experian, TransUnion — formal CROA/FCRA disputes with method-of-verification escalation on every item.' },
        { title: 'Original Data Furnishers', body: 'The actual creditor, servicer, collection agency, or lender reporting the item. We force them to prove ownership, authorization and balance accuracy.' },
        { title: 'Secondary Reporting Agencies', body: 'LexisNexis, SageStream, ARS, CoreLogic, Innovis — the back-end repositories the bureaus pull from. Removing it from the bureau is not enough if the source is still feeding it back.' },
        { title: 'CFPB & State AG Complaints', body: 'When furnishers stonewall, we escalate through the Consumer Financial Protection Bureau and State Attorney General filings. That makes the dispute a federal paper trail.' },
        { title: 'Student Loan Servicers Directly', body: 'Nelnet, Navient, MOHELA, Aidvantage, Sallie Mae and the rest — direct account-level challenges on balance, authorization, and documentation.' },
        { title: 'Debt Validation & Statute Review', body: 'FDCPA debt validation letters, statute of limitations review, and estoppel-by-silence escalations on anything that cannot be documented.' },
      ].map((d, i) => (
        <div key={i} className="sde-dispute-card" data-testid={`sde-dispute-${i}`}>
          <div className="sde-dispute-num">{String(i + 1).padStart(2, '0')}</div>
          <h3>{d.title}</h3>
          <p>{d.body}</p>
        </div>
      ))}
    </div>
  </section>
);

/* -------- Positions You For $250K -------- */
export const Positions250K = () => (
  <section className="sde-funding" data-testid="sde-funding">
    <div className="sde-funding-card">
      <div className="sde-funding-badge">
        <Landmark size={28} />
      </div>
      <span className="sde-eyebrow sde-eyebrow-gold">What Happens After We Clean The File</span>
      <h2 className="sde-section-title">
        Positions you for <span className="sde-gold">up to $250,000</span> in business funding.
      </h2>
      <p className="sde-section-sub">
        A clean file is step one — but a clean file sitting idle is just a prettier number.
        Once your reports are rebuilt, our funding team activates multiple lanes in parallel:
        business credit cards, revolving lines, term loans, SBA-adjacent facilities, and
        revenue-based financing — stacking up to $250K deployable capital in your name.
      </p>
      <div className="sde-funding-row">
        <div className="sde-funding-step">
          <div className="sde-funding-step-num">01</div>
          <h4>File Cleaned</h4>
          <p>Every removable negative gone. Scores positioned 700+ with the depth banks demand.</p>
        </div>
        <div className="sde-funding-step">
          <div className="sde-funding-step-num">02</div>
          <h4>Funding Lanes Opened</h4>
          <p>Business credit cards, revolving lines, term loans — matched to your profile and income.</p>
        </div>
        <div className="sde-funding-step">
          <div className="sde-funding-step-num">03</div>
          <h4>Capital Deployed</h4>
          <p>Up to $250,000 in combined approvals — deployed into your business, real estate, or next move.</p>
        </div>
      </div>
    </div>
  </section>
);

/* -------- Tailored Plan Form -------- */
export const TailoredPlanForm = ({ service, serviceLabel, onComplete }) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    creditScoreRange: '',
    biggestObstacle: '',
    fundingGoal: '',
    timeline: '',
    notes: '',
  });
  const [status, setStatus] = useState({ loading: false, done: false, error: '' });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, done: false, error: '' });
    try {
      const res = await fetch(`${API}/plan-intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service, ...form }),
      });
      if (!res.ok) throw new Error('Submit failed');
      await res.json();
      setStatus({ loading: false, done: true, error: '' });
      if (onComplete) onComplete();
    } catch (err) {
      setStatus({ loading: false, done: false, error: 'Something went wrong. Please try again.' });
    }
  };

  if (status.done) {
    return (
      <section className="sde-form-section" data-testid="sde-plan-form-success">
        <div className="sde-form-success">
          <ShieldCheck size={38} />
          <h3>Your plan is being tailored.</h3>
          <p>
            Thanks{form.firstName ? `, ${form.firstName}` : ''}. A Collins Academy strategist
            will reach out within 24 business hours to walk you through your personalized
            {serviceLabel ? ` ${serviceLabel}` : ''} roadmap. Scroll down to secure your spot.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="sde-form-section" data-testid="sde-plan-form">
      <div className="sde-form-head">
        <span className="sde-eyebrow sde-eyebrow-gold">Step 1 · Tailor Your Plan</span>
        <h2 className="sde-section-title">
          Build me a plan that <span className="sde-gold">fits my situation.</span>
        </h2>
        <p className="sde-section-sub">
          No cookie-cutter upsell. Answer the quick questions below and a strategist will
          build a plan specifically around <strong>your</strong> file, income, and goal.
        </p>
      </div>

      <form className="sde-form" onSubmit={submit} data-testid="sde-plan-form-el">
        <div className="sde-form-row">
          <label className="sde-field">
            <span><User size={13} /> First Name</span>
            <input
              required
              value={form.firstName}
              onChange={update('firstName')}
              type="text"
              autoComplete="given-name"
              data-testid="sde-form-firstName"
            />
          </label>
          <label className="sde-field">
            <span><User size={13} /> Last Name</span>
            <input
              required
              value={form.lastName}
              onChange={update('lastName')}
              type="text"
              autoComplete="family-name"
              data-testid="sde-form-lastName"
            />
          </label>
        </div>

        <div className="sde-form-row">
          <label className="sde-field">
            <span><Mail size={13} /> Email</span>
            <input
              required
              value={form.email}
              onChange={update('email')}
              type="email"
              autoComplete="email"
              data-testid="sde-form-email"
            />
          </label>
          <label className="sde-field">
            <span><Phone size={13} /> Phone</span>
            <input
              required
              value={form.phone}
              onChange={update('phone')}
              type="tel"
              autoComplete="tel"
              data-testid="sde-form-phone"
            />
          </label>
        </div>

        <div className="sde-form-row">
          <label className="sde-field">
            <span>Current Credit Score Range</span>
            <div className="sde-select-wrap">
              <select
                required
                value={form.creditScoreRange}
                onChange={update('creditScoreRange')}
                data-testid="sde-form-creditScoreRange"
              >
                <option value="">Select…</option>
                <option>Below 500</option>
                <option>500 – 579</option>
                <option>580 – 639</option>
                <option>640 – 699</option>
                <option>700 – 749</option>
                <option>750+</option>
                <option>I'm not sure</option>
              </select>
              <ChevronDown size={14} className="sde-select-icon" />
            </div>
          </label>
          <label className="sde-field">
            <span>Funding Goal</span>
            <div className="sde-select-wrap">
              <select
                required
                value={form.fundingGoal}
                onChange={update('fundingGoal')}
                data-testid="sde-form-fundingGoal"
              >
                <option value="">Select…</option>
                <option>Up to $50K</option>
                <option>$50K – $100K</option>
                <option>$100K – $250K</option>
                <option>$250K+</option>
                <option>Just want a clean file</option>
              </select>
              <ChevronDown size={14} className="sde-select-icon" />
            </div>
          </label>
        </div>

        <div className="sde-form-row">
          <label className="sde-field">
            <span><Target size={13} /> Biggest Obstacle</span>
            <div className="sde-select-wrap">
              <select
                required
                value={form.biggestObstacle}
                onChange={update('biggestObstacle')}
                data-testid="sde-form-biggestObstacle"
              >
                <option value="">Select…</option>
                <option>Student loans dragging my score</option>
                <option>Collections / charge-offs</option>
                <option>Late payments</option>
                <option>Hard inquiries</option>
                <option>Bankruptcy or public records</option>
                <option>Low score with no negatives</option>
                <option>Something else</option>
              </select>
              <ChevronDown size={14} className="sde-select-icon" />
            </div>
          </label>
          <label className="sde-field">
            <span>Timeline</span>
            <div className="sde-select-wrap">
              <select
                required
                value={form.timeline}
                onChange={update('timeline')}
                data-testid="sde-form-timeline"
              >
                <option value="">Select…</option>
                <option>ASAP — ready now</option>
                <option>Within 30 days</option>
                <option>1 – 3 months</option>
                <option>Just exploring</option>
              </select>
              <ChevronDown size={14} className="sde-select-icon" />
            </div>
          </label>
        </div>

        <label className="sde-field sde-field-wide">
          <span>Anything else we should know? (optional)</span>
          <textarea
            value={form.notes}
            onChange={update('notes')}
            rows={3}
            data-testid="sde-form-notes"
            placeholder="E.g. I have 4 student loans with Nelnet, score 580, trying to buy a home in Q2…"
          />
        </label>

        {status.error && <p className="sde-form-error">{status.error}</p>}

        <button
          type="submit"
          className="sde-form-submit"
          disabled={status.loading}
          data-testid="sde-form-submit"
        >
          {status.loading ? (
            <>Preparing your plan… <Loader2 size={16} className="sde-spin" /></>
          ) : (
            <>Get My Tailored Plan <ArrowRight size={16} /></>
          )}
        </button>

        <p className="sde-form-micro">
          <ShieldCheck size={13} /> Your info is encrypted and used only to build your plan.
          No spam, no selling your data.
        </p>
      </form>
    </section>
  );
};

/* -------- Agency Marquee (rotating banner) -------- */
export const AgencyMarquee = ({ agencies = [], label = 'Who We Cover' }) => {
  // Duplicate the list so the CSS animation loops seamlessly
  const loop = [...agencies, ...agencies];
  return (
    <div className="sde-marquee" data-testid="sde-agency-marquee">
      <div className="sde-marquee-label">
        <Building2 size={13} /> {label}
      </div>
      <div className="sde-marquee-track-wrap">
        <div className="sde-marquee-track">
          {loop.map((a, i) => (
            <span key={i} className="sde-marquee-pill" data-testid={`sde-agency-${i}`}>{a}</span>
          ))}
        </div>
      </div>
    </div>
  );
};
